import { forwardRef, type Ref, useMemo } from 'react';

import { type FieldValues, type UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Select as WFSelect, type SelectProps } from '@wf/components';

import { rimmEntityToOption } from '../utils/rimmEntityToOption';
import { useRimmSubEntity } from '../hooks/useRimmSubEntity';

import { type RimmEntityType, type RimmSubEntityType } from '../data/rimmEntityEnums';
import { type TRimmEntity, type TRimmEntityOption } from '../data/rimmEntityTypes';

export type TRimmSubEntitySelectProps<T extends RimmSubEntityType, P extends RimmEntityType> = SelectProps<
  TRimmEntityOption[T]
> & {
  entity: T;
  parentEntity: P;
  code: string | number;
  outerRimmServerURL?: string;
  name: string;
  required?: boolean;
  withTag?: boolean;
  tagKey?: keyof TRimmEntity[T];
  hasAllOption?: boolean;
  setValue?: UseFormSetValue<FieldValues>;
};

const RimmSubEntitySelectComponent = <T extends RimmSubEntityType, P extends RimmEntityType>(
  {
    entity,
    parentEntity,
    code,
    outerRimmServerURL,
    onInputChange,
    withTag,
    hasAllOption,
    tagKey,
    placeholder,
    setValue,
    value,
    name,
    ...props
  }: TRimmSubEntitySelectProps<T, P>,
  ref: Ref<HTMLSelectElement> | null
) => {
  const { rimmEntities, loading } = useRimmSubEntity(entity, parentEntity, code, outerRimmServerURL);
  const { t } = useTranslation();

  const options = useMemo(() => rimmEntityToOption(rimmEntities, !!withTag, tagKey), [rimmEntities, withTag, tagKey]);

  if (hasAllOption) {
    options.unshift({ value: 'ALL', label: t('ALL') } as TRimmEntityOption[T]);
  }

  if (setValue && options?.length && value) {
    if ((withTag && !value.tag?.label) || (!withTag && !value.label?.description)) {
      const selectedOption = options.find((option) => option.value === value.value);

      if (selectedOption) {
        setValue?.(name, selectedOption);
      }
    }
  }

  return (
    <WFSelect
      {...props}
      noOptionsMessage={() => t('No options')}
      loadingMessage={() => t('Loading...')}
      placeholder={placeholder}
      isLoading={loading}
      options={options}
      value={value}
      name={name}
      ref={ref}
      t={t}
      isClearable
    />
  );
};

export const RimmSubEntitySelect = forwardRef(RimmSubEntitySelectComponent);
